import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  BRUKSSKEDE_TEMPLATE,
  BRUKSSKEDE_STATUS_LABELS,
  type BruksskedeStatus,
} from "@/lib/basProcess/constants";
import { Building2, Plus, Trash2, AlertTriangle } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";

interface Props {
  projectId: string;
}

interface Checklist {
  id: string;
  space_name: string;
  description: string | null;
}

interface Item {
  id: string;
  checklist_id: string;
  section: string;
  item_key: string;
  control_point: string;
  legal_reference: string | null;
  explanation: string | null;
  status: BruksskedeStatus;
  reference: string | null;
  comment: string | null;
  sort_order: number;
}

interface ResidualRisk {
  id: string;
  space_name: string | null;
  risk_description: string;
  handling: string | null;
  status: string;
}

export const BruksskedeChecklists: React.FC<Props> = ({ projectId }) => {
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [risks, setRisks] = useState<ResidualRisk[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newSpace, setNewSpace] = useState("");
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    const [cl, rr] = await Promise.all([
      supabase
        .from("bruksskede_checklists")
        .select("id, space_name, description")
        .eq("project_id", projectId)
        .order("created_at", { ascending: true }),
      supabase
        .from("residual_use_risks")
        .select("id, space_name, risk_description, handling, status")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false }),
    ]);
    if (cl.error) toast.error("Kunde inte läsa bruksskedet");
    const lists = (cl.data || []) as Checklist[];
    setChecklists(lists);
    setRisks((rr.data || []) as ResidualRisk[]);
    if (lists.length && !activeId) setActiveId(lists[0].id);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    const loadItems = async () => {
      if (!activeId) return setItems([]);
      const { data, error } = await supabase
        .from("bruksskede_checklist_items")
        .select("*")
        .eq("checklist_id", activeId)
        .order("sort_order", { ascending: true });
      if (error) toast.error("Kunde inte läsa kontrollpunkter");
      else setItems((data || []) as Item[]);
    };
    loadItems();
  }, [activeId]);

  const createChecklist = async () => {
    if (!newSpace.trim()) return;
    const { data, error } = await supabase
      .from("bruksskede_checklists")
      .insert({ project_id: projectId, space_name: newSpace.trim() })
      .select("id")
      .single();
    if (error || !data) return toast.error("Kunde inte skapa checklistan");

    let order = 0;
    const rows = BRUKSSKEDE_TEMPLATE.flatMap((sec) =>
      sec.items.map((it) => ({
        checklist_id: data.id,
        section: sec.section,
        item_key: it.key,
        control_point: it.controlPoint,
        legal_reference: it.legalReference ?? null,
        explanation: it.explanation ?? null,
        sort_order: order++,
      }))
    );
    const { error: itemErr } = await supabase.from("bruksskede_checklist_items").insert(rows);
    if (itemErr) toast.error("Kunde inte skapa kontrollpunkter");

    setNewSpace("");
    setActiveId(data.id);
    await loadAll();
    toast.success("Checklista skapad");
  };

  const removeChecklist = async (id: string) => {
    await supabase.from("bruksskede_checklists").delete().eq("id", id);
    setActiveId(null);
    await loadAll();
  };

  const updateItem = async (id: string, patch: Partial<Item>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    const { error } = await supabase.from("bruksskede_checklist_items").update(patch as any).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const createRiskFromItem = async (item: Item) => {
    const list = checklists.find((c) => c.id === item.checklist_id);
    const { error } = await supabase.from("residual_use_risks").insert({
      project_id: projectId,
      checklist_id: item.checklist_id,
      checklist_item_id: item.id,
      space_name: list?.space_name ?? null,
      risk_description: item.control_point,
      handling: item.comment,
      legal_reference: item.legal_reference,
    });
    if (error) return toast.error("Kunde inte skapa bruksrisk");
    toast.success("Kvarvarande bruksrisk skapad");
    await loadAll();
  };

  const removeRisk = async (id: string) => {
    setRisks((prev) => prev.filter((r) => r.id !== id));
    await supabase.from("residual_use_risks").delete().eq("id", id);
  };

  const answered = items.filter((i) => i.status !== "unanswered").length;
  const progress = items.length ? Math.round((answered / items.length) * 100) : 0;
  const sections = Array.from(new Set(items.map((i) => i.section)));

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar bruksskedet...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="02 / 31"
        title="Bruksskede – funktionskrav och kvarvarande risker"
        description="Checklista per utrymme för framtida drift, underhåll och bruk."
        icon={Building2}
        actions={
          <div className="flex gap-2">
            <Input
              className="w-56"
              placeholder="Nytt utrymme, t.ex. Fläktrum plan 5"
              value={newSpace}
              onChange={(e) => setNewSpace(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createChecklist()}
            />
            <Button onClick={createChecklist}><Plus className="h-4 w-4 mr-1" />Lägg till</Button>
          </div>
        }
      />

      {checklists.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <Building2 className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Inga utrymmen tillagda ännu. Lägg till ett utrymme för att få hela funktionskravschecklistan.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {checklists.map((c) => (
              <Button
                key={c.id}
                size="sm"
                variant={activeId === c.id ? "default" : "outline"}
                onClick={() => setActiveId(c.id)}
              >
                {c.space_name}
              </Button>
            ))}
          </div>

          {activeId && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CardTitle className="text-base">
                    {checklists.find((c) => c.id === activeId)?.space_name}
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    <div className="w-40">
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>Besvarat</span><span>{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeChecklist(activeId)}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {sections.map((section) => (
                  <div key={section} className="space-y-3">
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                      {section}
                    </h4>
                    <ul className="divide-y">
                      {items.filter((i) => i.section === section).map((item) => (
                        <li key={item.id} className="py-3 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{item.control_point}</span>
                            {item.legal_reference && (
                              <Badge variant="secondary" className="text-[10px]">{item.legal_reference}</Badge>
                            )}
                          </div>
                          {item.explanation && (
                            <p className="text-sm text-muted-foreground">{item.explanation}</p>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {(Object.keys(BRUKSSKEDE_STATUS_LABELS) as BruksskedeStatus[]).map((s) => (
                              <Button
                                key={s}
                                size="sm"
                                variant={
                                  item.status === s ? (s === "deviation" ? "destructive" : "default") : "outline"
                                }
                                onClick={() => updateItem(item.id, { status: s })}
                              >
                                {BRUKSSKEDE_STATUS_LABELS[s]}
                              </Button>
                            ))}
                            {item.status === "deviation" && (
                              <Button size="sm" variant="ghost" onClick={() => createRiskFromItem(item)}>
                                <AlertTriangle className="h-4 w-4 mr-1" />
                                Skapa bruksrisk
                              </Button>
                            )}
                          </div>
                          <div className="grid gap-2 sm:grid-cols-3">
                            <Input
                              placeholder="Hänvisning (ritning/handling)"
                              defaultValue={item.reference ?? ""}
                              onBlur={(e) => updateItem(item.id, { reference: e.target.value })}
                            />
                            <Textarea
                              className="sm:col-span-2"
                              rows={1}
                              placeholder="Kommentar"
                              defaultValue={item.comment ?? ""}
                              onBlur={(e) => updateItem(item.id, { comment: e.target.value })}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Kvarvarande bruksrisker</CardTitle>
          <p className="text-sm text-muted-foreground">
            Risker som lämnas över till förvaltaren enligt dokument 31.
          </p>
        </CardHeader>
        <CardContent>
          {risks.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Inga kvarvarande bruksrisker registrerade.</p>
          ) : (
            <ul className="divide-y">
              {risks.map((r) => (
                <li key={r.id} className="py-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      {r.space_name && <Badge variant="outline" className="text-[10px]">{r.space_name}</Badge>}
                      <span className="font-medium">{r.risk_description}</span>
                    </div>
                    {r.handling && <p className="text-sm text-muted-foreground mt-0.5">{r.handling}</p>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeRisk(r.id)}>
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BruksskedeChecklists;
